// The parser behind /mrz-checker: any pasted machine-readable zone in, every
// field and every check digit's verdict out.
//
// It reads the three ICAO 9303 layouts a travel document or identity card
// carries — TD3 (two lines of 44, passports), TD2 (two lines of 36) and TD1
// (three lines of 30) — and the two visa layouts that share TD3's and TD2's
// line lengths but carry no composite digit (MRV-A and MRV-B). A visa is told
// apart by its document code, which starts with V; without that a pasted visa
// would parse as a passport whose composite digit is missing, which is a wrong
// answer rather than a clear one.
//
// Everything here is a pure function over the pasted text, so the page can run
// it in the browser on every keystroke and the unit suite can run it on the
// same strings. The one input that is not text is "now", which decides whether
// an expiry date is in the past and which century a date of birth is in; it is
// a parameter so a test can fix it.
//
// Positions in messages are 1-based, the way the standard numbers them and the
// way a person counts characters on a card. The slicing below is 0-based.

import { checkDigit, TD1_LINE_LENGTH, TD2_LINE_LENGTH, TD3_LINE_LENGTH } from "./mrz";

export type MrzFormat = "TD1" | "TD2" | "TD3" | "MRV-A" | "MRV-B";

/** One field read out of the zone. */
export interface MrzField {
  readonly key: string;
  readonly label: string;
  /** The value as a person reads it: fillers removed, dates spelled out. */
  readonly value: string;
  /** The characters exactly as the zone prints them. */
  readonly raw: string;
  /** Where the field sits, e.g. "line 2, 1–9". */
  readonly where: string;
}

/** One check digit: what the zone prints and what the data says it should be. */
export interface MrzCheck {
  readonly key: string;
  readonly label: string;
  /** Where the digit itself sits, e.g. "line 2, 10". */
  readonly where: string;
  /** The character the zone prints as the digit. */
  readonly printed: string;
  /** The digit recomputed from the data, or null when the data holds a character no zone may. */
  readonly computed: number | null;
  readonly valid: boolean;
}

export interface MrzParseResult {
  /** True when the text is a zone of a known layout and was read. */
  readonly ok: boolean;
  readonly format: MrzFormat | null;
  /** The layout in words, e.g. "TD3 — two lines of 44 (passport)". */
  readonly formatName: string;
  /** The lines that were read, after the clean-up described in `notes`. */
  readonly lines: readonly string[];
  /** Why the text could not be read as a zone. Empty when `ok`. */
  readonly errors: readonly string[];
  /** Read, but something in it is wrong or unusual. */
  readonly warnings: readonly string[];
  /** What was done to the pasted text before it was read. */
  readonly notes: readonly string[];
  readonly fields: readonly MrzField[];
  readonly checks: readonly MrzCheck[];
  /** True when every check digit matches. */
  readonly checksumsValid: boolean;
}

/** The key of every field the parser can read out, whichever layout it is in. */
export type MrzFieldKey =
  | "documentCode"
  | "issuingState"
  | "surname"
  | "givenNames"
  | "documentNumber"
  | "nationality"
  | "dateOfBirth"
  | "sex"
  | "expiryDate"
  | "personalNumber"
  | "optionalData"
  | "optionalDataLine1"
  | "optionalDataLine2"
  | "composite";

/**
 * Every word the parser puts in front of a person, in one language.
 *
 * The parser runs in the browser on every keystroke, so a page in another
 * language cannot translate its result after the fact: the sentences are built
 * from counts and positions as they are found. It takes the words instead, and
 * builds the same sentences in the page's language. Positions passed to the
 * functions are already 1-based. The English set is the default, so a caller
 * that passes nothing gets exactly the text it always got.
 */
export interface MrzParserText {
  readonly formatNames: Readonly<Record<MrzFormat, string>>;
  /** The three shapes a zone can have, said when the text has none of them. */
  readonly shapes: string;
  /** The codes ICAO 9303 part 3 uses that are not an ISO 3166-1 country code. */
  readonly specialCodes: Readonly<Record<string, string>>;
  readonly labels: Readonly<Record<MrzFieldKey, string>>;
  readonly spacesRemoved: string;
  readonly lowerCaseRead: string;
  readonly joinedLineSplit: (length: number, lines: number) => string;
  readonly nothingToRead: string;
  readonly lineCounts: (lengths: readonly number[]) => string;
  readonly looksLike: (format: string, lineLength: number, problems: readonly string[]) => string;
  readonly lineShort: (line: number, missing: number) => string;
  readonly lineLong: (line: number, extra: number) => string;
  /** A place in the zone: one position, or a range when `to` is given. */
  readonly position: (line: number, from: number, to?: number) => string;
  /** A TD1 document number that overflows into the optional data. */
  readonly overflowPosition: (lastPosition: number) => string;
  readonly overflowWarning: string;
  readonly documentKinds: {
    readonly passport: string;
    readonly visa: string;
    readonly card: string;
  };
  readonly sexes: { readonly male: string; readonly female: string; readonly unspecified: string };
  readonly expired: string;
  readonly notExpired: string;
  readonly nameTruncated: (line: number, from: number, to: number) => string;
  readonly notACalendarDate: (label: string, raw: string, where: string) => string;
  readonly unexpectedSex: (raw: string, where: string) => string;
  readonly badCharacter: (char: string, line: number, position: number) => string;
  readonly badCharacters: (found: string, more: number) => string;
}

const SHAPES_SENTENCE =
  "A machine-readable zone is two lines of 44 characters (TD3, passports), two lines of 36 " +
  "(TD2) or three lines of 30 (TD1).";

const plural = (count: number, word: string) => `${word}${count === 1 ? "" : "s"}`;

export const MRZ_PARSER_TEXT_EN: MrzParserText = {
  formatNames: {
    TD3: "TD3 – two lines of 44 (passport)",
    TD2: "TD2 – two lines of 36 (identity or travel card)",
    TD1: "TD1 – three lines of 30 (identity card or residence permit)",
    "MRV-A": "MRV-A – two lines of 44 (visa)",
    "MRV-B": "MRV-B – two lines of 36 (visa)",
  },
  shapes: SHAPES_SENTENCE,
  specialCodes: {
    D: "Germany",
    UTO: "Utopia (ICAO specimen)",
    EUE: "European Union",
    UNO: "United Nations",
    XXA: "Stateless person",
    XXB: "Refugee (1951 Convention)",
    XXC: "Refugee (other)",
    XXX: "Unspecified nationality",
  },
  labels: {
    documentCode: "Document code",
    issuingState: "Issuing state",
    surname: "Surname",
    givenNames: "Given names",
    documentNumber: "Document number",
    nationality: "Nationality",
    dateOfBirth: "Date of birth",
    sex: "Sex",
    expiryDate: "Expiry date",
    personalNumber: "Personal number",
    optionalData: "Optional data",
    optionalDataLine1: "Optional data (line 1)",
    optionalDataLine2: "Optional data (line 2)",
    composite: "Composite",
  },
  spacesRemoved: "Spaces inside a line were removed; a machine-readable zone has none.",
  lowerCaseRead: "Lower-case letters were read as capitals; a machine-readable zone has none.",
  joinedLineSplit: (length, lines) =>
    `One line of ${length} characters was split into ${lines} lines.`,
  nothingToRead: "Nothing to read yet.",
  lineCounts: (lengths) => {
    const described = lengths.map((length, i) => `line ${i + 1} has ${length}`).join(", ");
    return `Found ${lengths.length} ${plural(lengths.length, "line")} (${described} characters).`;
  },
  looksLike: (format, lineLength, problems) =>
    `This looks like a ${format} zone, whose lines are ${lineLength} characters each, but ` +
    `${problems.join(" and ")}. A character was probably lost or added when it was copied; ` +
    "trailing < fillers count.",
  lineShort: (line, missing) => `line ${line} is ${missing} ${plural(missing, "character")} short`,
  lineLong: (line, extra) => `line ${line} is ${extra} ${plural(extra, "character")} too long`,
  position: (line, from, to) =>
    to === undefined ? `line ${line}, ${from}` : `line ${line}, ${from}–${to}`,
  overflowPosition: (lastPosition) => `line 1, 6–14 and 16–${lastPosition}`,
  overflowWarning:
    "The document number is longer than nine characters: it continues in the optional data " +
    "on line 1, and its check digit sits after the last of its characters.",
  documentKinds: { passport: "passport", visa: "visa", card: "identity or travel card" },
  sexes: { male: "male", female: "female", unspecified: "unspecified" },
  expired: "expired",
  notExpired: "not expired",
  nameTruncated: (line, from, to) =>
    `The name fills line ${line} to its last character (positions ${from}–${to}), so it may ` +
    "have been truncated to fit.",
  notACalendarDate: (label, raw, where) => `${label} ${raw} is not a calendar date (${where}).`,
  unexpectedSex: (raw, where) => `Sex is "${raw}" (${where}); a zone prints M, F or < there.`,
  badCharacter: (char, line, position) => `"${char}" at line ${line}, ${position}`,
  badCharacters: (found, more) =>
    `A zone holds only A–Z, 0–9 and the filler <. Found ${found}` +
    `${more > 0 ? ` and ${more} more` : ""}; the check digits over those fields cannot be computed.`,
};

// ---- the text a person pasted ---------------------------------------------

interface CleanLines {
  readonly lines: string[];
  readonly notes: string[];
}

/**
 * The lines of the zone, cleaned the way the standard allows: surrounding
 * whitespace dropped, spaces inside a line removed (an OCR copy or a PDF often
 * inserts them), lower case read as capitals (a zone has none), and a single
 * line of exactly two or three zone lines' length split back into lines.
 *
 * What it does NOT do is guess at a misread character — an O for a 0, a « for
 * a <. That is the check digits' job, and a clean-up that "fixed" a character
 * would hide the very mistake the digits are there to catch.
 */
export function cleanMrzLines(text: string, t: MrzParserText = MRZ_PARSER_TEXT_EN): CleanLines {
  const notes: string[] = [];
  let lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.some((line) => /\s/.test(line))) {
    lines = lines.map((line) => line.replace(/\s+/g, ""));
    notes.push(t.spacesRemoved);
  }
  if (lines.some((line) => /[a-z]/.test(line))) {
    lines = lines.map((line) => line.toUpperCase());
    notes.push(t.lowerCaseRead);
  }

  const only = lines[0];
  if (lines.length === 1 && only !== undefined) {
    const split = splitJoined(only);
    if (split !== null) {
      lines = split;
      notes.push(t.joinedLineSplit(only.length, split.length));
    }
  }
  return { lines, notes };
}

// A zone copied out of a response's `mrz.text`, or out of a field that dropped
// the line breaks, arrives as one line of 88, 72 or 90 characters.
function splitJoined(line: string): string[] | null {
  const chunk = (size: number) =>
    Array.from({ length: line.length / size }, (_, i) => line.slice(i * size, (i + 1) * size));
  if (line.length === 2 * TD3_LINE_LENGTH) return chunk(TD3_LINE_LENGTH);
  if (line.length === 2 * TD2_LINE_LENGTH) return chunk(TD2_LINE_LENGTH);
  if (line.length === 3 * TD1_LINE_LENGTH) return chunk(TD1_LINE_LENGTH);
  return null;
}

// ---- which layout ---------------------------------------------------------

/** The layout a set of cleaned lines is in, or why it is in none. */
export function detectMrzFormat(
  lines: readonly string[],
  t: MrzParserText = MRZ_PARSER_TEXT_EN,
): { format: MrzFormat; error: null } | { format: null; error: string } {
  const lengths = lines.map((line) => line.length);
  const all = (length: number) => lengths.every((l) => l === length);
  const visa = (lines[0] ?? "").startsWith("V");

  if (lines.length === 0) {
    return { format: null, error: `${t.nothingToRead} ${t.shapes}` };
  }
  if (lines.length === 3 && all(TD1_LINE_LENGTH)) return { format: "TD1", error: null };
  if (lines.length === 2 && all(TD2_LINE_LENGTH)) {
    return { format: visa ? "MRV-B" : "TD2", error: null };
  }
  if (lines.length === 2 && all(TD3_LINE_LENGTH)) {
    return { format: visa ? "MRV-A" : "TD3", error: null };
  }
  return { format: null, error: shapeError(lengths, t) };
}

// The message for text that is no layout: which line is off, by how much, and
// against which layout — the nearest one, since that is the one the person was
// almost certainly pasting.
function shapeError(lengths: readonly number[], t: MrzParserText): string {
  const count = lengths.length;
  if (count === 2 || count === 3) {
    const candidates = count === 3 ? [TD1_LINE_LENGTH] : [TD2_LINE_LENGTH, TD3_LINE_LENGTH];
    const longest = Math.max(...lengths);
    const target = candidates.reduce((best, length) =>
      Math.abs(length - longest) < Math.abs(best - longest) ? length : best,
    );
    const name = target === TD1_LINE_LENGTH ? "TD1" : target === TD2_LINE_LENGTH ? "TD2" : "TD3";
    const off = lengths
      .map((length, i) => ({ length, line: i + 1 }))
      .filter(({ length }) => length !== target)
      .map(({ length, line }) => {
        const gap = target - length;
        return gap > 0 ? t.lineShort(line, gap) : t.lineLong(line, -gap);
      });
    return t.looksLike(name, target, off);
  }
  return `${t.lineCounts(lengths)} ${t.shapes}`;
}

// ---- reading the fields ---------------------------------------------------

function trimFiller(value: string): string {
  return value.replace(/<+$/g, "").replace(/^<+/g, "");
}

function span(t: MrzParserText, line: number, start: number, end: number): string {
  return end - start === 1 ? t.position(line, start + 1) : t.position(line, start + 1, end);
}

function field(
  t: MrzParserText,
  key: string,
  label: string,
  line: string,
  lineNumber: number,
  start: number,
  end: number,
  value: (raw: string) => string = (raw) => trimFiller(raw) || "–",
): MrzField {
  const raw = line.slice(start, end);
  return { key, label, value: value(raw), raw, where: span(t, lineNumber, start, end) };
}

function check(
  t: MrzParserText,
  key: string,
  label: string,
  over: string,
  line: string,
  lineNumber: number,
  position: number,
  fillerAllowed = false,
): MrzCheck {
  const printed = line.charAt(position);
  const computed = checkDigit(over);
  // A field made only of fillers — an optional field nobody used — may carry
  // a filler as its digit instead of the zero the arithmetic gives.
  const blank = /^<*$/.test(over);
  const valid =
    computed !== null &&
    (printed === String(computed) || (fillerAllowed && blank && printed === "<"));
  return {
    key,
    label,
    where: span(t, lineNumber, position, position + 1),
    printed,
    computed,
    valid,
  };
}

function codeMeaning(raw: string, t: MrzParserText): string {
  const code = trimFiller(raw);
  const letter = code.charAt(0);
  const kind =
    letter === "P"
      ? t.documentKinds.passport
      : letter === "V"
        ? t.documentKinds.visa
        : letter === "I" || letter === "A" || letter === "C"
          ? t.documentKinds.card
          : null;
  if (code === "") return "–";
  return kind === null ? code : `${code} (${kind})`;
}

function stateName(raw: string, names: Readonly<Record<string, string>>, t: MrzParserText): string {
  const code = trimFiller(raw);
  if (code === "") return "–";
  const name = t.specialCodes[code] ?? names[code];
  return name === undefined ? code : `${code} – ${name}`;
}

function nameValue(raw: string): { surname: string; givenNames: string } {
  const trimmed = raw.replace(/<+$/g, "");
  const separator = trimmed.indexOf("<<");
  const words = (part: string) => part.replace(/</g, " ").replace(/\s+/g, " ").trim();
  if (separator < 0) return { surname: words(trimmed), givenNames: "" };
  return {
    surname: words(trimmed.slice(0, separator)),
    givenNames: words(trimmed.slice(separator + 2)),
  };
}

function sexValue(raw: string, t: MrzParserText): string {
  if (raw === "M") return `M (${t.sexes.male})`;
  if (raw === "F") return `F (${t.sexes.female})`;
  if (raw === "<" || raw === "X") return `${raw} (${t.sexes.unspecified})`;
  return raw;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** A YYMMDD date as a calendar date, or null when it is not one. */
export function readMrzDate(
  raw: string,
  kind: "birth" | "expiry",
  now: Date,
): { iso: string; words: string } | null {
  if (!/^\d{6}$/.test(raw)) return null;
  const yy = Number(raw.slice(0, 2));
  const month = Number(raw.slice(2, 4));
  const day = Number(raw.slice(4, 6));
  // YYMMDD has no century. A date of birth is in the past, so it takes the
  // latest century that keeps it there; an expiry date is read as this
  // century, since a document that expired before 2000 is not in anyone's
  // hands to be read.
  //
  // "In the past" is decided on the whole date, not the year: in September a
  // birth date of this year's December has not happened yet, so it belongs to
  // the previous century, and comparing years alone would put it in this one.
  // The strings compare correctly as text because both are zero-padded
  // YYYY-MM-DD. A birth date of today is in the past for this purpose.
  let year = 2000 + yy;
  if (kind === "birth") {
    const today = now.toISOString().slice(0, 10);
    if (`${year}-${raw.slice(2, 4)}-${raw.slice(4, 6)}` > today) year -= 100;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (month < 1 || month > 12 || date.getUTCDate() !== day || date.getUTCMonth() !== month - 1) {
    return null;
  }
  const iso = `${year}-${raw.slice(2, 4)}-${raw.slice(4, 6)}`;
  return { iso, words: `${day} ${MONTHS[month - 1]} ${year}` };
}

function dateValue(raw: string, kind: "birth" | "expiry", now: Date, t: MrzParserText): string {
  const read = readMrzDate(raw, kind, now);
  if (read === null) return raw.replace(/</g, "?");
  if (kind === "expiry") {
    const today = now.toISOString().slice(0, 10);
    return `${read.iso} (${read.iso < today ? t.expired : t.notExpired})`;
  }
  return read.iso;
}

// ---- the layouts ----------------------------------------------------------

interface Read {
  fields: MrzField[];
  checks: MrzCheck[];
  warnings: string[];
}

// TD3 and the MRV-A visa share the upper line and most of the lower one: the
// document code, the issuing state and the name above; below, the number, the
// nationality, the birth date, the sex and the expiry, each at the same place.
// They differ after the expiry: TD3 has a personal number with its own digit and
// a composite; a visa has optional data and no digit at all. TD2 and MRV-B are
// the same pair, eight characters narrower.
function readTwoLine(
  format: "TD3" | "TD2" | "MRV-A" | "MRV-B",
  upper: string,
  lower: string,
  now: Date,
  names: Readonly<Record<string, string>>,
  t: MrzParserText,
): Read {
  const width = upper.length;
  const person = nameValue(upper.slice(5, width));
  const fields: MrzField[] = [
    field(t, "documentCode", t.labels.documentCode, upper, 1, 0, 2, (raw) => codeMeaning(raw, t)),
    field(t, "issuingState", t.labels.issuingState, upper, 1, 2, 5, (raw) =>
      stateName(raw, names, t),
    ),
    field(t, "surname", t.labels.surname, upper, 1, 5, width, () => person.surname || "–"),
    field(t, "givenNames", t.labels.givenNames, upper, 1, 5, width, () => person.givenNames || "–"),
    field(t, "documentNumber", t.labels.documentNumber, lower, 2, 0, 9),
    field(t, "nationality", t.labels.nationality, lower, 2, 10, 13, (raw) =>
      stateName(raw, names, t),
    ),
    field(t, "dateOfBirth", t.labels.dateOfBirth, lower, 2, 13, 19, (raw) =>
      dateValue(raw, "birth", now, t),
    ),
    field(t, "sex", t.labels.sex, lower, 2, 20, 21, (raw) => sexValue(raw, t)),
    field(t, "expiryDate", t.labels.expiryDate, lower, 2, 21, 27, (raw) =>
      dateValue(raw, "expiry", now, t),
    ),
  ];
  const checks: MrzCheck[] = [
    check(t, "documentNumber", t.labels.documentNumber, lower.slice(0, 9), lower, 2, 9),
    check(t, "dateOfBirth", t.labels.dateOfBirth, lower.slice(13, 19), lower, 2, 19),
    check(t, "expiryDate", t.labels.expiryDate, lower.slice(21, 27), lower, 2, 27),
  ];

  if (format === "TD3") {
    fields.push(field(t, "personalNumber", t.labels.personalNumber, lower, 2, 28, 42));
    checks.push(
      check(t, "personalNumber", t.labels.personalNumber, lower.slice(28, 42), lower, 2, 42, true),
      // The composite skips the nationality (11–13) and the sex (21): the two
      // things a digit never protects.
      check(
        t,
        "composite",
        t.labels.composite,
        lower.slice(0, 10) + lower.slice(13, 20) + lower.slice(21, 43),
        lower,
        2,
        43,
      ),
    );
  } else if (format === "TD2") {
    fields.push(field(t, "optionalData", t.labels.optionalData, lower, 2, 28, 35));
    checks.push(
      check(
        t,
        "composite",
        t.labels.composite,
        lower.slice(0, 10) + lower.slice(13, 20) + lower.slice(21, 35),
        lower,
        2,
        35,
      ),
    );
  } else {
    fields.push(field(t, "optionalData", t.labels.optionalData, lower, 2, 28, width));
  }

  const warnings = [
    ...nameWarnings(upper.slice(5, width), 1, 6, t),
    ...fieldWarnings(fields, now, t),
  ];
  return { fields, checks, warnings };
}

// TD1 puts the number on the upper line, the dates, sex and nationality on the
// middle one, and gives the name a line of its own. Its composite is the only
// one that spans two lines.
function readTd1(
  upper: string,
  middle: string,
  lower: string,
  now: Date,
  names: Readonly<Record<string, string>>,
  t: MrzParserText,
): Read {
  const person = nameValue(lower);
  const warnings: string[] = [];

  // A document number longer than nine characters overflows into the optional
  // data after it: the nine principal characters stay in 6–14, position 15
  // carries a filler instead of a digit, and the rest of the number follows in
  // 16 onwards with its check digit as the last character before the fillers.
  // The digit is then computed over the whole number.
  let numberField = field(t, "documentNumber", t.labels.documentNumber, upper, 1, 5, 14);
  let numberCheck = check(
    t,
    "documentNumber",
    t.labels.documentNumber,
    upper.slice(5, 14),
    upper,
    1,
    14,
  );
  let optionalStart = 15;
  const overflow = upper.charAt(14) === "<" ? /^[A-Z0-9]+/.exec(upper.slice(15))?.[0] : undefined;
  if (overflow !== undefined && overflow.length >= 2) {
    const rest = overflow.slice(0, -1);
    const whole = upper.slice(5, 14) + rest;
    const digitAt = 15 + overflow.length - 1;
    numberField = {
      key: "documentNumber",
      label: t.labels.documentNumber,
      value: trimFiller(whole),
      raw: upper.slice(5, digitAt),
      where: t.overflowPosition(digitAt),
    };
    numberCheck = check(t, "documentNumber", t.labels.documentNumber, whole, upper, 1, digitAt);
    optionalStart = digitAt + 1;
    warnings.push(t.overflowWarning);
  }

  const fields: MrzField[] = [
    field(t, "documentCode", t.labels.documentCode, upper, 1, 0, 2, (raw) => codeMeaning(raw, t)),
    field(t, "issuingState", t.labels.issuingState, upper, 1, 2, 5, (raw) =>
      stateName(raw, names, t),
    ),
    numberField,
    field(t, "optionalData", t.labels.optionalDataLine1, upper, 1, optionalStart, 30),
    field(t, "dateOfBirth", t.labels.dateOfBirth, middle, 2, 0, 6, (raw) =>
      dateValue(raw, "birth", now, t),
    ),
    field(t, "sex", t.labels.sex, middle, 2, 7, 8, (raw) => sexValue(raw, t)),
    field(t, "expiryDate", t.labels.expiryDate, middle, 2, 8, 14, (raw) =>
      dateValue(raw, "expiry", now, t),
    ),
    field(t, "nationality", t.labels.nationality, middle, 2, 15, 18, (raw) =>
      stateName(raw, names, t),
    ),
    field(t, "optionalData2", t.labels.optionalDataLine2, middle, 2, 18, 29),
    field(t, "surname", t.labels.surname, lower, 3, 0, 30, () => person.surname || "–"),
    field(t, "givenNames", t.labels.givenNames, lower, 3, 0, 30, () => person.givenNames || "–"),
  ];
  const checks: MrzCheck[] = [
    numberCheck,
    check(t, "dateOfBirth", t.labels.dateOfBirth, middle.slice(0, 6), middle, 2, 6),
    check(t, "expiryDate", t.labels.expiryDate, middle.slice(8, 14), middle, 2, 14),
    // Upper line 6–30, then middle line 1–7, 9–15 and 19–29: the document code,
    // the issuing state, the sex and the nationality are all outside it.
    check(
      t,
      "composite",
      t.labels.composite,
      upper.slice(5, 30) + middle.slice(0, 7) + middle.slice(8, 15) + middle.slice(18, 29),
      middle,
      2,
      29,
    ),
  ];
  warnings.push(...nameWarnings(lower, 3, 1, t), ...fieldWarnings(fields, now, t));
  return { fields, checks, warnings };
}

// A name that reaches the last character of its zone has probably been cut to
// fit; the standard truncates rather than wraps, and the printed page carries
// the whole name.
function nameWarnings(
  zone: string,
  line: number,
  firstPosition: number,
  t: MrzParserText,
): string[] {
  if (zone.length > 0 && !zone.endsWith("<")) {
    return [t.nameTruncated(line, firstPosition, firstPosition + zone.length - 1)];
  }
  return [];
}

function fieldWarnings(fields: readonly MrzField[], now: Date, t: MrzParserText): string[] {
  const warnings: string[] = [];
  const byKey = new Map(fields.map((f) => [f.key, f]));
  for (const [key, kind] of [
    ["dateOfBirth", "birth"],
    ["expiryDate", "expiry"],
  ] as const) {
    const f = byKey.get(key);
    if (f !== undefined && /^\d{6}$/.test(f.raw) && readMrzDate(f.raw, kind, now) === null) {
      warnings.push(t.notACalendarDate(f.label, f.raw, f.where));
    }
  }
  const sex = byKey.get("sex");
  if (sex !== undefined && !["M", "F", "X", "<"].includes(sex.raw)) {
    warnings.push(t.unexpectedSex(sex.raw, sex.where));
  }
  return warnings;
}

// Characters no zone may contain, each with its place, so a person can find
// the one to fix. The check digits over those fields cannot be computed at all.
function characterWarnings(lines: readonly string[], t: MrzParserText): string[] {
  const found: string[] = [];
  lines.forEach((line, index) => {
    for (let position = 0; position < line.length; position++) {
      const char = line.charAt(position);
      if (!/[A-Z0-9<]/.test(char)) {
        found.push(t.badCharacter(char, index + 1, position + 1));
      }
    }
  });
  if (found.length === 0) return [];
  const shown = found.slice(0, 5).join(", ");
  return [t.badCharacters(shown, Math.max(0, found.length - 5))];
}

// ---- the whole thing ------------------------------------------------------

/**
 * Reads a pasted machine-readable zone.
 *
 * `names` maps ISO 3166-1 alpha-3 codes to country names, for the issuing state
 * and the nationality; it is a parameter so this module does not carry a
 * country table of its own.
 */
export function parseMrz(
  text: string,
  options: {
    now?: Date;
    names?: Readonly<Record<string, string>>;
    /** The words of the result; English when not given. */
    text?: MrzParserText;
  } = {},
): MrzParseResult {
  const now = options.now ?? new Date();
  const names = options.names ?? {};
  const t = options.text ?? MRZ_PARSER_TEXT_EN;
  const { lines, notes } = cleanMrzLines(text, t);
  const detected = detectMrzFormat(lines, t);
  if (detected.format === null) {
    return {
      ok: false,
      format: null,
      formatName: "",
      lines,
      errors: [detected.error],
      warnings: [],
      notes,
      fields: [],
      checks: [],
      checksumsValid: false,
    };
  }

  const format = detected.format;
  const read =
    format === "TD1"
      ? readTd1(lines[0] ?? "", lines[1] ?? "", lines[2] ?? "", now, names, t)
      : readTwoLine(format, lines[0] ?? "", lines[1] ?? "", now, names, t);

  return {
    ok: true,
    format,
    formatName: t.formatNames[format],
    lines,
    errors: [],
    warnings: [...characterWarnings(lines, t), ...read.warnings],
    notes,
    fields: read.fields,
    checks: read.checks,
    checksumsValid: read.checks.every((c) => c.valid),
  };
}
